import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import clsx from "clsx";
import parse from "format-message-parse";
import { bindActionCreators, Dispatch } from "redux";

import { Form, TextInput, TextArea, InputSize, ValidationMessage } from "@plex/react-components";

import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { messageFormatActions } from "../MessageId.actions";
import { TokenType, getTokenParameters, validateMessageId, validateMessageTextWithTokens } from "../MessageId.helpers";
import styles from "../MessageId.module.scss";
import { IMessageIdState, IToken, IValidationResult } from "../MessageId.state";

interface IMessageFormatSectionProps {
  messageIdParameter: string;
  messageId: string;
  text: string;
  currentStatus: string;
  messageIdValidationError: string;
  textValidationError: string;
  changeMessageIdField: (messageId: string) => void;
  changeTextField: (messageId: string) => void;
  addTokens: (tokens: IToken[]) => void;
  changeTextPreview: (textPreview: string) => void;
  localizeValidationResult: (result?: IValidationResult) => string;
  setTextValidationError: (error: string) => void;
  setMessageIdValidationError: (error: string) => void;
  setValidationBanner: (flag: boolean) => void;
}

const MessageFormatSection: React.FC<IMessageFormatSectionProps> = ({
  messageIdParameter,
  messageId,
  text,
  currentStatus,
  messageIdValidationError,
  textValidationError,
  changeMessageIdField,
  changeTextField,
  addTokens,
  localizeValidationResult,
  changeTextPreview,
  setTextValidationError,
  setMessageIdValidationError,
  setValidationBanner
}) => {
  const localizer = useLocalizer();

  const localizedValues = useLocalizedValues(() => ({
    messageIdLabel: displayMessages.property("messageId"),
    textLabel: displayMessages.property("text"),
    statusLabel: displayMessages.property("status"),
    messageFormatHeading: displayMessages.heading("messageFormat")
  }));

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const [textTokens, setMessageTokens] = useState<parse.Token[]>([]);
  const [tokenParameters, setTokenParameters] = useState<IToken[]>([]);
  const [textIsValid, setTextIsValid] = useState<boolean>(true);

  const onMessageIdBlur = useCallback(() => {
    const result: IValidationResult = validateMessageId(messageId);
    setValidationBanner(!result.success);
    setMessageIdValidationError(localizeValidationResult(result));
  }, [messageId, setMessageIdValidationError, setValidationBanner, localizeValidationResult]);

  const onTextBlur = useCallback(() => {
    const result: IValidationResult = validateMessageTextWithTokens(text, tokenParameters);
    if (result.success) {
      addTokens(tokenParameters);
      changeTextPreview(text);
      setValidationBanner(false);
    } else {
      addTokens([]);
      changeTextPreview("");
      setValidationBanner(true);
    }

    setTextValidationError(localizeValidationResult(result));
  }, [text, setTextValidationError, localizeValidationResult, addTokens, tokenParameters, changeTextPreview, setValidationBanner]);

  const onTextChange = useCallback(
    (value: string | null) => {
      const textValue: string = value || "";
      const tokens: parse.Token[] = [];

      try {
        const textElements: parse.Element[] = parse(textValue, { tokens: tokens });
        const parameters: IToken[] = getTokenParameters(textElements);
        setTokenParameters(parameters);
        setTextIsValid(true);
      } catch (error) {
        setTextIsValid(false);
      }

      setMessageTokens(tokens);
      changeTextField(textValue);
    },
    [changeTextField]
  );

  useEffect(() => {
    onTextChange(text);
  }, [text, onTextChange]);

  const onMessageIdChange = (value: string | null): void => {
    changeMessageIdField(value || "");
  };

  const renderMessageTextItems = () => {
    let validTextLastIndex = 0;
    const textItems = textTokens.map((token, i) => {
      if (token[1]) {
        validTextLastIndex += token[1].length;
      }

      if (token[0] === "text" || token[0] === "space") {
        return token[1];
      }

      let className = styles.icuM;
      switch (token[0]) {
        case TokenType.id:
          className = styles.icuMArgname;
          break;
        case TokenType.type:
          className = styles.icuMArgtype;
          break;
        case TokenType.style:
          className = styles.icuMArgstyle;
          break;
        case TokenType.selector:
          className = styles.icuMArgkey;
          break;
        case TokenType.syntax:
          className = token[1] === "#" ? styles.icuMArghash : styles.icuMArg;
          break;
        default:
          className = styles.icuMArg;
          break;
      }
      return (
        <span key={i} className={className}>
          {token[1]}
        </span>
      );
    });

    if (!textIsValid && text.length > 0) {
      textItems.push(text.slice(validTextLastIndex));
    }

    return textItems;
  };

  const renderStatusField = (
    <Form.Row>
      <Form.FieldPair labelText={localizedValues.statusLabel}>
        <div className={styles.messageFormatInputContainer}>
          <TextInput key="statusField" value={currentStatus && localizer({ value: currentStatus })} className={styles.messageFormatInput} disabled />
        </div>
      </Form.FieldPair>
    </Form.Row>
  );

  const renderTextField = (
    <Form.Row>
      <Form.FieldPair required labelText={localizedValues.textLabel}>
        <div className={clsx(styles.formPattern, styles.messageFormatInputContainer)}>
          <TextArea
            className={clsx(styles.formText, textValidationError && styles.invalidField, styles.messageFormatInput)}
            id="messageText"
            key="textArea"
            name="messageText"
            autoGrow
            value={text}
            onValueChange={onTextChange}
            onBlur={onTextBlur}
            disabled={!roles.PlexTechnicalWriter}
          />
          <code className={clsx(styles.icuM, styles.formMessage)}>{renderMessageTextItems()}</code>
        </div>
        <ValidationMessage message={textValidationError} />
      </Form.FieldPair>
    </Form.Row>
  );

  return (
    <Form.Section title={localizedValues.messageFormatHeading}>
      <Form.Row>
        <Form.FieldPair required labelText={localizedValues.messageIdLabel}>
          <div className={styles.messageFormatInputContainer}>
            <TextInput
              id="messageIdInput"
              name="messageIdInput"
              className={clsx(styles.messageFormatInput, messageIdValidationError && styles.invalidField)}
              maxLength={150}
              value={messageId}
              size={InputSize.xlarge}
              onValueChange={onMessageIdChange}
              onBlur={onMessageIdBlur}
              disabled={!!messageIdParameter}
            />
            <ValidationMessage message={messageIdValidationError} />
          </div>
        </Form.FieldPair>
      </Form.Row>
      {renderTextField}
      {messageIdParameter && renderStatusField}
    </Form.Section>
  );
};

export default connect(
  (state: { messageFormat: IMessageIdState }) => {
    return {
      messageId: state.messageFormat.messageId,
      text: state.messageFormat.text,
      currentStatus: state.messageFormat.currentStatus,
      messageIdValidationError: state.messageFormat.messageIdValidationError,
      textValidationError: state.messageFormat.textValidationError
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        changeMessageIdField: messageFormatActions.changeMessageIdField,
        changeTextField: messageFormatActions.changeTextField,
        addTokens: messageFormatActions.addTokens,
        changeTextPreview: messageFormatActions.changeTextPreview,
        setTextValidationError: messageFormatActions.setTextValidationError,
        setMessageIdValidationError: messageFormatActions.setMessageIdValidationError,
        setValidationBanner: messageFormatActions.setValidationBanner
      },
      dispatch
    )
)(MessageFormatSection);
